import _ from 'lodash';
import i18n from 'i18n';
import { Fields } from './DownloadReportPanelModel';

export type OutdoorCustomFieldSelection = { [field: string]: boolean };

/** 資料內容的欄位，依介面示意圖的分列方式。畫面直接照這份渲染。 */
export const OUTDOOR_DATA_CONTENT_ROWS: Fields[][] = [
  [Fields.IMPRES],
  [Fields.VIEW, Fields.VIEW_RATE],
  [Fields.SPENT],
  [Fields.CPM, Fields.ACPM],
  [Fields.BEACON_EVENTS, Fields.E_PUBLISHER_IMPRES]
];

export const OUTDOOR_VIDEO_FIELDS = [
  Fields.FIRST_QUARTILE,
  Fields.MID_POINT,
  Fields.THIRD_QUARTILE,
  Fields.COMPLETE
];

/**
 * xlsx 的欄序完全跟隨送出的 metrics 順序（後端不重排），所以送出的順序是從
 * 畫面用的那兩份欄位表推導出來的 —— 改動畫面排法就會一併改到送出的順序，
 * 兩邊不會各自漂開。
 */
export const OUTDOOR_FIELD_ORDER = [
  ..._.flatten(OUTDOOR_DATA_CONTENT_ROWS),
  ...OUTDOOR_VIDEO_FIELDS
];

export const OutdoorQuickSelect = {
  CPM: 'cpm',
  ACPM: 'acpm',
  RESET: 'reset'
} as const;

export type OutdoorQuickSelectType =
  (typeof OutdoorQuickSelect)[keyof typeof OutdoorQuickSelect];

export const OUTDOOR_QUICK_SELECT_FIELDS: {
  [type: string]: Fields[];
} = {
  [OutdoorQuickSelect.CPM]: [
    Fields.IMPRES,
    Fields.VIEW,
    Fields.VIEW_RATE,
    Fields.CPM,
    Fields.SPENT
  ],
  [OutdoorQuickSelect.ACPM]: [
    Fields.IMPRES,
    Fields.VIEW,
    Fields.E_PUBLISHER_IMPRES,
    Fields.ACPM,
    Fields.SPENT
  ],
  [OutdoorQuickSelect.RESET]: []
};

/**
 * key 是分母欄位，value 是需要它才算得出來的欄位。
 * aCPM = 已花費 ÷ 人流數 × 1000，CPM = 已花費 ÷ 曝光數 × 1000，
 * 觀看率 = 觀看數 ÷ 曝光數。
 *
 * 這不只是提示：後端產 xlsx 時，公式裡的欄位 token 只會替換成「有被勾選的欄位」的座標，
 * 缺席的 token 會原樣留在公式字串裡變成壞公式。所以分母沒勾就不能讓這些欄位送出去。
 */
export const OUTDOOR_FIELD_DEPENDENCIES: { [field: string]: Fields[] } = {
  [Fields.IMPRES]: [Fields.VIEW_RATE, Fields.CPM],
  [Fields.VIEW]: [Fields.VIEW_RATE],
  [Fields.SPENT]: [Fields.CPM, Fields.ACPM],
  [Fields.E_PUBLISHER_IMPRES]: [Fields.ACPM]
};

const fieldLabel = (field: string) =>
  i18n.t(`downloadReportPanel.labels.${_.camelCase(field)}`);

export const initialOutdoorFieldSelection = (): OutdoorCustomFieldSelection =>
  OUTDOOR_FIELD_ORDER.reduce(
    (acc, field) => ({ ...acc, [field]: false }),
    {} as OutdoorCustomFieldSelection
  );

export const outdoorQuickSelectSelection = (
  type: OutdoorQuickSelectType
): OutdoorCustomFieldSelection => {
  const picked = OUTDOOR_QUICK_SELECT_FIELDS[type] ?? [];
  return OUTDOOR_FIELD_ORDER.reduce(
    (acc, field) => ({ ...acc, [field]: picked.includes(field) }),
    {} as OutdoorCustomFieldSelection
  );
};

export const outdoorFieldErrors = (
  selection: OutdoorCustomFieldSelection
): { [field: string]: string } =>
  Object.keys(OUTDOOR_FIELD_DEPENDENCIES).reduce<{ [field: string]: string }>(
    (acc, denominator) => {
      if (selection[denominator]) {
        return acc;
      }
      const orphans = OUTDOOR_FIELD_DEPENDENCIES[denominator].filter(
        (dependent) => selection[dependent]
      );
      if (orphans.length === 0) {
        return acc;
      }
      return {
        ...acc,
        [denominator]: i18n.t('downloadReportPanel.errors.fieldRequired', {
          field: fieldLabel(denominator),
          checkedDependentFields: orphans.map(fieldLabel).join(', ')
        })
      };
    },
    {}
  );

/** 送出的 metrics —— 新分頁唯一的產生點。 */
export const selectedOutdoorMetrics = (
  selection: OutdoorCustomFieldSelection
): Fields[] => OUTDOOR_FIELD_ORDER.filter((field) => selection[field]);

export const canDownloadOutdoorCustomReport = (
  selection: OutdoorCustomFieldSelection
): boolean =>
  selectedOutdoorMetrics(selection).length > 0 &&
  _.isEmpty(outdoorFieldErrors(selection));
